import type { z } from 'zod';

import type { Device, Side } from '../../utils';
import { SIDES, atDevice } from '../../utils';
import { createVersionedFeature } from '../create-versioned-feature';
import { borderSideV0Schema, borderV0Schema } from './v0';

import { isOffEverywhere } from '../../compact';

/** The side a contour gets when it is switched on without one of its own. */
export const createBorderSide = (): z.output<typeof borderSideV0Schema> => ({
    width: 1,
    style: 'solid',
    color: '#000',
});

/**
 * The contour a box draws around itself.
 *
 * Shared by the **block** and the **column** — the two entities that draw one,
 * with the same shape down to the side defaults. The section has no contour of
 * its own: a header row is painted by its block.
 */
export const borderFeature = createVersionedFeature({
    versionMap: {
        0: { schema: borderV0Schema },
    },
    latestVersion: 0,
    createDefault: (): z.output<typeof borderV0Schema> => ({
        version: 0,
        isEnabled: { desktop: false },
    }),
});

export type BorderSettings = z.output<typeof borderFeature.latestSchema>;
export type BorderSide = z.output<typeof borderSideV0Schema>;

const sameSide = (a: BorderSide | undefined, b: BorderSide | undefined): boolean =>
    a?.width === b?.width && a?.style === b?.style && a?.color === b?.color;

/**
 * Whether the contour is drawn side by side at `device` — the answer legacy kept
 * in `isIndependentBorders`, computed from the sides instead of stored beside
 * them.
 *
 * Nothing about the drawing depends on it: the CSS emits all four sides either
 * way. It only decides whether the editor shows one set of controls or four,
 * which is why it belongs to the moment the panel is drawn and not to the entity.
 */
export const isBorderIndependent = (
    border: Pick<BorderSettings, Side> | undefined,
    device: Device = 'desktop'
): boolean => {
    if (!border) {
        return false;
    }

    const top = atDevice(border.top, device);

    return !SIDES.every((side) => sameSide(atDevice(border[side], device), top));
};

/** Drops the sides of a contour that is off at every breakpoint — nothing reads them. */
export const compactBorder = <T extends Partial<BorderSettings>>(border: T): T => {
    if (!isOffEverywhere(border.isEnabled) || SIDES.every((side) => !(side in border))) {
        return border;
    }

    const kept = { ...border };
    SIDES.forEach((side) => delete kept[side]);

    return kept;
};

export { borderV0Schema, borderSideV0Schema };

export {
    borderSideValueVar,
    borderSideVar,
    borderVars,
    borderWidthVar,
    borderWidthVars,
    legacyBorderSideVar,
} from './border.css-vars';
export type { LegacyBorderBag } from './border.css-vars';
